import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Dropdown, Option } from 'src/components/Forms/Dropdown';
import { Fields } from 'src/components/CustomisationPage/feature/customisation.types';
import { useFormApi } from 'src/core/forms';
import { Colour, Font, formValuesToString, useFontPreview } from 'src/components/CustomisationPage/utils/fontPreview';
import { RxFormContext } from 'src/core/forms';
import { Input } from 'src/components/Forms/Input';
import { basicPresenceValidation } from 'src/util/forms';
import { Fieldset } from 'src/components/Forms/Fieldset';
import { map, tap } from 'rxjs';
import { Field } from 'src/components/Forms/Field';
import { Skeleton } from 'src/components/Common/Skeleton';
import { expand } from 'src/core/forms/utils/expand';
import { CommonFooter } from 'src/components/CustomisationPage/steps/CommonFooter';
import { Spinner } from 'src/components/Layout/Spinner';
import classnames from 'classnames';
import { BackgroundStyles } from 'src/components/BundleProduct/MultipleStepCustomisation/customisations.types';
import { FONT_PREVIEW_URL } from 'src/constants/customisation';

import classes from './FontPreview.scss';

type Props = {
    initialValues: { [index: string]: any };
    fonts: Font[];
    colours: Colour[];
    fieldPrefix?: string;
    isBundleView?: boolean;
    backgroundStyles?: BackgroundStyles;
};

export const FontPreview: React.FC<Props> = ({
    initialValues,
    fonts,
    colours,
    fieldPrefix = '',
    isBundleView,
    backgroundStyles,
}) => {
    const formApi = useFormApi();

    const field = useCallback((fieldname: string) => `${fieldPrefix}${fieldname}`, [fieldPrefix]);
    const setFont = (value) => formApi.setValue(field('font'), value);
    const setColour = (value) => formApi.setValue(field('color'), value);

    const fontOptions: Option[] = fonts.map((f) => {
        return {
            image: f.preview,
            name: f.name,
            value: f.name,
        };
    });
    const colourOptions: Option[] = colours.map((c) => {
        return {
            name: c.name,
            value: c.name,
            swatch: '#' + c.fg_colour,
        };
    });

    const font = initialValues?.[Fields.font] || formApi.getValue(field('font'));
    const validFont = fonts.find((f) => f.name === font);
    const color = initialValues?.[Fields.color] || formApi.getValue(field('color'));
    const validColor = colours.find((f) => f.name === color);

    useEffect(() => {
        if (!validFont) {
            setTimeout(() => formApi.setValue(field('font'), fonts[0].name), 1000);
        }
        if (!validColor) {
            setTimeout(() => formApi.setValue(field('color'), colours[colours.length - 1].name), 1000);
        }
    }, [field, formApi, colours, fonts, validColor, validFont]);

    return (
        <Fieldset disabled={false}>
            <div className={classnames({ [classes.wrapper]: !isBundleView })} style={backgroundStyles}>
                <Input
                    label="Text Line 1"
                    field={field(Fields.textLineOne)}
                    id={Fields.textLineOne}
                    validate={basicPresenceValidation()}
                    required={true}
                    placeholder="e.g Workwear Express"
                />
                <Input label="Text Line 2" field={field(Fields.textLineTwo)} id={Fields.textLineTwo} optional={true} />
                <Input
                    label="Text Line 3"
                    field={field(Fields.textLineThree)}
                    id={Fields.textLineThree}
                    optional={true}
                />

                <div className={classes.action}>
                    <div className={classes.dropdown}>
                        <Dropdown
                            options={fontOptions}
                            label="Font"
                            field={field(Fields.font)}
                            onChange={setFont}
                            initialValue={validFont ? validFont.name : fonts[0].name}
                            showOnlyImage
                        />
                    </div>
                    <div className={classes.dropdown}>
                        <Dropdown
                            options={colourOptions}
                            label="Colour"
                            field={field(Fields.color)}
                            onChange={setColour}
                            initialValue={validColor?.name || 'White'}
                        />
                    </div>
                </div>
            </div>
            <div className={classnames({ [classes.wrapper]: !isBundleView })} style={backgroundStyles}>
                <InlineImage fieldPrefix={fieldPrefix} />
            </div>
            <CommonFooter fieldPrefix={fieldPrefix} isBundleView={isBundleView} backgroundStyles={backgroundStyles} />
        </Fieldset>
    );
};

const InlineImage: React.FC<{ fieldPrefix?: string }> = (props) => {
    const { initialValues, getValueStream } = useContext(RxFormContext);

    const initial = props.fieldPrefix ? initialValues[props.fieldPrefix] || {} : initialValues;
    const [query, setQuery] = useState(formValuesToString(initial));

    /**
     * This will subscribe to form changes and only set state
     * after a small interval of time
     */
    useEffect(() => {
        const sub = getValueStream()
            .pipe(
                // debounceTime(1000),
                map(expand),
                map((values) => {
                    if (props.fieldPrefix) {
                        // if the prefix ended in a dot '.',  remove it
                        const selector = props.fieldPrefix.slice(0, -1);

                        const customizationNumber = selector.slice(0, 1);
                        const customizationObject = values[customizationNumber];
                        const customizastionInfo = customizationObject[selector.slice(2)];

                        return formValuesToString(customizastionInfo);
                    }
                    return formValuesToString(values);
                }),
                tap(setQuery),
            )
            .subscribe();
        return () => sub.unsubscribe();
    }, [getValueStream, props.fieldPrefix]);

    return (
        <Field>
            <p>Text Preview</p>
            <div className={classes.imagePreview}>
                {query.includes('&') ? <img src={`${FONT_PREVIEW_URL}?${query}`} alt="Font preview" /> : <Spinner />}
            </div>
        </Field>
    );
};

export const FontPreviewConnected: React.FC<{
    initialValues: { [index: string]: any };
    fieldPrefix?: string;
    isBundleView?: boolean;
    backgroundStyles?: BackgroundStyles;
}> = ({ fieldPrefix, initialValues, isBundleView, backgroundStyles }) => {
    const { fonts, pending, colours } = useFontPreview();

    if (pending) return <Skeleton />;

    return (
        <FontPreview
            initialValues={initialValues}
            fieldPrefix={fieldPrefix}
            fonts={fonts}
            colours={colours}
            isBundleView={isBundleView}
            backgroundStyles={backgroundStyles}
        />
    );
};
